import { createRouteParams } from "@prismicio/editor-support/URL"
import {
  type Document as DocumentT,
  fillDocumentWithDefaultValues as internalFillDocumentWithDefaultValues,
  GroupContent,
  traverseDocument,
} from "@prismicio/types-internal/lib/content"
import {
  flattenSections,
  type StaticCustomType,
} from "@prismicio/types-internal/lib/customtypes"

import { createEmptyGroupItemContent } from "../zones/RepeatableZoneEditor"
import { getLinkContent, getRepeatableContent } from "./repeatable"
import type { DocumentVersion } from "./service"

// Note: Why do we even bother preparing the document's data once we loaded it?
// Prismic never updates the document data proactively once it's been saved so this means a user could update a custom type
// and all the documents that were saved with the old custom type in mind are now a bit outdated/weird.
// This function makes sure we fix the document based on the latest custom type at least when users go back to it and save it.
export function prepareDocument(
  document: DocumentT,
  customType: StaticCustomType,
): DocumentT {
  let preparedDocument = document

  preparedDocument = convertRepeatableContent(preparedDocument, customType)
  preparedDocument = addDefaultGroupItems(preparedDocument, customType)
  preparedDocument = maskContentWithModel(preparedDocument, customType)
  preparedDocument = fillDocumentWithDefaultValues(preparedDocument, customType)

  return preparedDocument
}

function addDefaultGroupItems(
  document: DocumentT,
  customType: StaticCustomType,
): DocumentT {
  const fields = flattenSections(customType)

  return fields.reduce<DocumentT>((filledDocument, [fieldKey, fieldDef]) => {
    const fieldContent = document[fieldKey]

    const noContentForGroup = !fieldContent && fieldDef.type === "Group"

    if (noContentForGroup)
      return {
        ...filledDocument,
        [fieldKey]: defaultGroupContentWithOneItem(),
      }

    return filledDocument
  }, document)
}

// We don't want to see outdated content in the API if the custom type is no longer compatible.
function maskContentWithModel(
  document: DocumentT,
  customType: StaticCustomType,
) {
  return traverseDocument({
    document,
    customType,
  })({
    transformWidget({ model, content }) {
      return model ? content : undefined
    },
    transformSlice(slice) {
      return slice.model ? slice.content : undefined
    },
  })
}

function convertRepeatableContent(
  document: DocumentT,
  customType: StaticCustomType,
) {
  return traverseDocument({ document, customType })({
    // @ts-expect-error -- TODO: FIXME: https://linear.app/prismic/issue/DT-2521
    // This needs to be fixed in prismic-types-internal.
    // Operationally, traverseDocument currently allows us to transform content
    // (e.g. LinkContent -> RepeatableContent), but the types do not support it.
    transformWidget({ model, content }) {
      if (model?.type === "Link") {
        const { repeat } = model.config ?? {}
        switch (content.__TYPE__) {
          case "LinkContent": {
            if (!repeat) return content
            // LinkContent with {repeat: true} -> RepeatableContent
            return getRepeatableContent(content) ?? content
          }
          case "RepeatableContent": {
            if (repeat) return content
            // RepeatableContent with {repeat: false} -> LinkContent
            return getLinkContent(content) ?? content
          }
          default: {
            return content
          }
        }
      }
      return content
    },
  })
}

function fillDocumentWithDefaultValues(
  document: DocumentT,
  customType: StaticCustomType,
) {
  return internalFillDocumentWithDefaultValues(customType, document)
}

export function defaultGroupContentWithOneItem(): GroupContent {
  return {
    __TYPE__: "GroupContentType",
    value: [createEmptyGroupItemContent()],
  }
}

// Single source of truth for any visual Prismic document status mapping.
export const documentStatus = {
  unclassified: {
    title: "Draft",
    color: "amber",
  },
  published: {
    title: "Live",
    color: "green",
  },
  release: {
    title: "Planned",
    color: "purple",
  },
  archived: {
    title: "Archived",
    color: "grey",
  },
} as const

export const searchDocumentStatus: Record<
  DocumentVersion["status"],
  DocumentVersion["status"][]
> = {
  unclassified: ["unclassified", "published"],
  archived: ["unclassified", "published"],
  release: ["release", "published"],
  published: ["published", "unclassified"],
}

interface getDocumentUrlArgs {
  documentId: string
  status?: string
  releaseId?: string
  versionId?: string
  baseUrl: URL
}

export function getDocumentUrl(args: getDocumentUrlArgs): URL {
  const { documentId, versionId, baseUrl, status, releaseId } = args

  const params = { s: status, v: versionId, r: releaseId }
  const newSearchParams = createRouteParams({ params })
  const urlParams = new URLSearchParams(newSearchParams).toString()

  return new URL(`pages/${documentId}?${urlParams}`, baseUrl)
}
